import { useEffect, useMemo, useState } from 'react'
import type { CSSProperties, FC } from 'react'
import { useQuery } from '@tanstack/react-query'

import { Drawer } from '@/components'
import { rqKeys } from '@/react-query-keys'
import { fetchVisitorMessageTopics, findVisitorMessageTopics, useMessagesStore } from '../index'
import type { T_VisitorMessageTopicEntry } from '../types'
import { MessagesTopicList } from './messages-topic-list'
import { MessagesTopicView } from './messages-topic-view'
import {
  SC_BackButton,
  SC_MessagesConversationPane,
  SC_MessagesLayout,
  SC_MessagesTopicsPane,
  SC_TopicHeaderMobile,
  SC_TopicHeaderTitle,
} from './styled'

type T_MessagesDrawerProps = {
  onClose: () => void
}

const toTimestampMs = (value: unknown): number => {
  if (value instanceof Date) {
    const ts = value.getTime()
    return Number.isFinite(ts) ? ts : 0
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0
  }

  if (typeof value === 'string' && value.trim()) {
    const parsed = Date.parse(value)
    if (Number.isFinite(parsed)) return parsed
  }

  return 0
}

const useDesktopMode = (breakpointPx = 600) => {
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window === 'undefined') return false
    return window.matchMedia(`(min-width: ${breakpointPx}px)`).matches
  })

  useEffect(() => {
    if (typeof window === 'undefined') return

    const media = window.matchMedia(`(min-width: ${breakpointPx}px)`)
    const onChange = (e: MediaQueryListEvent) => setIsDesktop(e.matches)

    setIsDesktop(media.matches)
    media.addEventListener('change', onChange)

    return () => media.removeEventListener('change', onChange)
  }, [ breakpointPx ])

  return isDesktop
}

export const MessagesDrawer: FC<T_MessagesDrawerProps> = ({ onClose }) => {
  const isDesktop = useDesktopMode()
  const topicActivityMap = useMessagesStore((s) => s.topicActivityMap)
  const activeTopicId = useMessagesStore((s) => s.activeTopicId)
  const activeMessageId = useMessagesStore((s) => s.activeMessageId)

  const [ searchText, setSearchText ] = useState('')
  const [ debouncedSearch, setDebouncedSearch ] = useState('')
  const [ selectedTopicId, setSelectedTopicId ] = useState<number | null>(activeTopicId || null)
  const [ isTopicViewVisible, setIsTopicViewVisible ] = useState<boolean>(!!activeTopicId || isDesktop)

  const {
    data: topics,
    isPending: isTopicsPending,
  } = useQuery({
    queryKey: [ rqKeys.visitorMessageTopics ],
    queryFn: fetchVisitorMessageTopics,
  })

  const {
    data: searchedTopics,
    isPending: isSearchedTopicsPending,
  } = useQuery({
    queryKey: [ rqKeys.visitorMessageTopics, 'search', debouncedSearch ],
    queryFn: () => findVisitorMessageTopics(debouncedSearch),
    enabled: !!debouncedSearch.trim(),
  })

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch(searchText.trim())
    }, 350)

    return () => window.clearTimeout(timeoutId)
  }, [ searchText ])

  useEffect(() => {
    setSelectedTopicId(activeTopicId || null)
    setIsTopicViewVisible(!!activeTopicId || isDesktop)
  }, [ activeTopicId, isDesktop ])

  const visibleTopics = useMemo(() => {
    const source = debouncedSearch ? (searchedTopics || []) : (topics || [])

    return [ ...source ]
      .map((topic) => ({
        ...topic,
        lastMessageTime: new Date(
          Math.max(
            toTimestampMs(topic.lastMessageTime),
            topicActivityMap[topic.id] || 0,
          )
        ),
      }))
      .sort((a, b) => b.lastMessageTime.getTime() - a.lastMessageTime.getTime())
  }, [ debouncedSearch, searchedTopics, topics, topicActivityMap ])

  useEffect(() => {
    if (selectedTopicId) {
      const stillExists = visibleTopics.some((topic) => topic.id === selectedTopicId)
      if (stillExists) return
    }

    if (visibleTopics.length) {
      setSelectedTopicId(visibleTopics[0].id)
      if (isDesktop) setIsTopicViewVisible(true)
    } else {
      setSelectedTopicId(null)
      if (!isDesktop) setIsTopicViewVisible(false)
    }
  }, [ visibleTopics, selectedTopicId, isDesktop ])

  const selectedTopic = useMemo<T_VisitorMessageTopicEntry | null>(() => {
    if (!selectedTopicId) return null
    return visibleTopics.find((topic) => topic.id === selectedTopicId) || null
  }, [ selectedTopicId, visibleTopics ])

  const isTopicListLoading = isTopicsPending || (isSearchedTopicsPending && !!debouncedSearch)
  const drawerContentStyle = useMemo<CSSProperties | undefined>(() => {
    if (!isDesktop) return undefined

    return {
      width: 'calc(100vw - 40px)',
      maxWidth: '1080px',
      height: 'min(82dvh, 720px)',
      top: '50%',
      bottom: 'auto',
      transform: 'translate(-50%, -50%)',
      borderRadius: '12px',
      padding: '14px 16px 16px',
      overflowY: 'hidden',
    }
  }, [ isDesktop ])

  return (
    <Drawer
      heading='Сообщения'
      isFullScreen={!isDesktop}
      isBackdropClosable
      contentStyle={drawerContentStyle}
      onClose={onClose}
    >
      <SC_MessagesLayout $isDesktop={isDesktop} $isTopicViewVisible={isTopicViewVisible}>
        <SC_MessagesTopicsPane $isDesktop={isDesktop} $isTopicViewVisible={isTopicViewVisible}>
          <MessagesTopicList
            topics={visibleTopics}
            selectedTopicId={selectedTopicId}
            searchText={searchText}
            isLoading={isTopicListLoading}
            onSearchTextChange={setSearchText}
            onSearchClear={() => {
              setSearchText('')
              setDebouncedSearch('')
            }}
            onSelectTopic={(topic) => {
              setSelectedTopicId(topic.id)
              if (!isDesktop) setIsTopicViewVisible(true)
            }}
          />
        </SC_MessagesTopicsPane>

        <SC_MessagesConversationPane $isDesktop={isDesktop} $isTopicViewVisible={isTopicViewVisible}>
          {!isDesktop && selectedTopic && (
            <SC_TopicHeaderMobile>
              <SC_BackButton
                type='button'
                onClick={() => setIsTopicViewVisible(false)}
                title='Назад'
              >
                ←
              </SC_BackButton>

              <SC_TopicHeaderTitle>
                <div>{selectedTopic.subject || 'Без темы'}</div>
                <div>{selectedTopic.author.fullname || selectedTopic.author.login || 'Диалог'}</div>
              </SC_TopicHeaderTitle>
            </SC_TopicHeaderMobile>
          )}

          <MessagesTopicView
            topicId={selectedTopicId}
            topicSubject={selectedTopic?.subject || null}
            selectedMessageId={activeMessageId}
          />
        </SC_MessagesConversationPane>
      </SC_MessagesLayout>
    </Drawer>
  )
}
